import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_CONVERSION_PLATFORM_SUBMISSIONS_QUERY,
  SINGLE_CONVERSION_PLATFORM_SUBMISSION_QUERY,
  CREATE_CONVERSION_PLATFORM_SUBMISSION_MUTATION,
  UPDATE_CONVERSION_PLATFORM_SUBMISSION_MUTATION,
  DELETE_CONVERSION_PLATFORM_SUBMISSION_MUTATION,
  FORCE_DELETE_CONVERSION_PLATFORM_SUBMISSION_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const trackingConversionPlatformSubmissionRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_CONVERSION_PLATFORM_SUBMISSION_QUERY, variables).then((data: any) => {
      return hydrateObject(data.conversionPlatformSubmission)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_CONVERSION_PLATFORM_SUBMISSIONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.conversionPlatformSubmissions)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_CONVERSION_PLATFORM_SUBMISSION_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.conversionPlatformSubmission),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_CONVERSION_PLATFORM_SUBMISSIONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.conversionPlatformSubmissions),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_CONVERSION_PLATFORM_SUBMISSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.conversionPlatformSubmission)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_CONVERSION_PLATFORM_SUBMISSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.conversionPlatformSubmission)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_CONVERSION_PLATFORM_SUBMISSION_MUTATION, variables).then((data: any) => {
      return data.deleteTrackingConversionPlatformSubmission
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_CONVERSION_PLATFORM_SUBMISSION_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTrackingConversionPlatformSubmission
    })
  },
}

export default trackingConversionPlatformSubmissionRepository
